import { useEffect } from 'react';
import { useSupportRealtime } from '../hooks/useSupportRealtime';
import { maybeInAppSupportNotify, registerSupportServiceWorker } from '../utils/supportPush';
import { parseSupportOpenMessage, requestOpenSupportTicket } from '../utils/supportOpen';

/**
 * Global portal support alerts while cabinet is open (any section).
 */
export function SupportNotifyBridge({
  enabled,
  activeNav,
  openSupport,
  onToast,
}: {
  enabled: boolean;
  activeNav: string;
  openSupport: (ticketId: number) => void;
  onToast?: (message: string, ticketId: number) => void;
}) {
  const onSupportNav = activeNav === 'support';
  const live = enabled && !onSupportNav;

  useEffect(() => {
    if (!enabled) return;
    void registerSupportServiceWorker(`${import.meta.env.BASE_URL}sw.js`);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const parsed = parseSupportOpenMessage(event.data);
      if (!parsed) return;
      openSupport(parsed.ticketId);
      window.setTimeout(
        () => requestOpenSupportTicket(parsed.ticketId, true),
        onSupportNav ? 0 : 120,
      );
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [enabled, openSupport, onSupportNav]);

  const { sendReceipt } = useSupportRealtime(
    (event) => {
      if (event.type !== 'ticket_snapshot' || !event.ticket) return;
      if (event.ticket.last_message_author_role === 'support' && event.ticket.last_message_id) {
        sendReceipt(event.ticket_id, 'delivered', event.ticket.last_message_id);
      }

      const go = () => {
        openSupport(event.ticket_id);
        window.setTimeout(() => requestOpenSupportTicket(event.ticket_id, true), 80);
      };

      if (event.ticket.last_message_author_role === 'support' && event.ticket.last_message_id) {
        const preview = event.ticket.last_message_preview || 'Новое сообщение';
        maybeInAppSupportNotify({
          title: event.ticket.status === 'closed' ? 'Тикет закрыт' : 'Ответ поддержки',
          body: `${event.ticket.subject}: ${preview}`,
          ticketId: event.ticket_id,
          selectedTicketId: null,
          dedupeKey: `portal-global-${event.ticket.last_message_id}`,
          onToast: (msg) => onToast?.(msg, event.ticket_id),
          onClick: go,
        });
        return;
      }
      if (event.ticket.status === 'closed') {
        maybeInAppSupportNotify({
          title: 'Тикет закрыт',
          body: event.ticket.subject,
          ticketId: event.ticket_id,
          selectedTicketId: null,
          dedupeKey: `portal-global-closed-${event.ticket_id}-${event.ticket.updated_at}`,
          onToast: (msg) => onToast?.(msg, event.ticket_id),
          onClick: go,
        });
      }
    },
    { enabled: live },
  );

  return null;
}
